import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useState } from "react";

import type { DashboardCardId } from "_shared/_views/dashboards/dashboard-card-order";

export type DashboardCardsMenuProps = {
  cards: readonly { id: DashboardCardId; label: string }[];
  hidden: readonly DashboardCardId[];
  /** Whether anything has been rearranged or put away, and so whether there is anything to undo. */
  isCustomised: boolean;
  onToggle: (cardId: DashboardCardId) => void;
  onReset: () => void;
};

/**
 * The one place a reader can put a card away, bring it back, or undo the lot.
 *
 * It exists because the arrangement is saved and invisible: without it, a reader who drags the
 * cards into something they dislike has no way back short of clearing the site's data, and a reader
 * who has ever dragged anything keeps their order forever — including past any later change to the
 * order the page ships with.
 *
 * It also answers a question the dashboard could not otherwise answer. Three cards render nothing
 * when the reader lacks the permission behind them, so "my queue is gone" is ambiguous between
 * having put it away and having lost access. This menu is the authoritative answer to the first
 * half: a card ticked here and still absent is a permission, not a preference. That is also why the
 * count of put-away cards rides on the button rather than living inside the menu — a dashboard
 * missing a card the reader forgot they hid should say so without being opened.
 */
export default function DashboardCardsMenu(props: Readonly<DashboardCardsMenuProps>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const hidden = props.hidden;
  const onToggle = props.onToggle;
  const onReset = props.onReset;

  const close = () => setAnchor(null);
  const reset = () => {
    onReset();
    close();
  };

  const hiddenCount = props.cards.filter((card) => hidden.includes(card.id)).length;
  const label = hiddenCount > 0
    ? `Choose cards. ${hiddenCount} of ${props.cards.length} put away.`
    : "Choose cards";

  return (
    <>
      <Badge badgeContent={hiddenCount} color="primary" overlap="circular">
        <IconButton aria-label={label} size="small" onClick={(event) => setAnchor(event.currentTarget)}>
          <TuneRoundedIcon fontSize="small" />
        </IconButton>
      </Badge>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {props.cards.map((card) => (
          // The menu stays open: putting several cards away in one go is the ordinary case, and a
          // menu that closes on each tick makes the reader reopen it every time.
          <MenuItem
            key={card.id}
            role="menuitemcheckbox"
            aria-checked={!hidden.includes(card.id)}
            onClick={() => onToggle(card.id)}
          >
            {/* A tick rather than a checkbox: a checkbox inside a menu item is a second control
                inside a control, with its own focus stop and its own label to keep in step. */}
            <ListItemIcon>
              {!hidden.includes(card.id) && <CheckRoundedIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{card.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem disabled={!props.isCustomised} onClick={reset}>
          <ListItemText>Reset to the default layout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
